import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Pencil,
  Camera,
  X,
  Star,
  Trash2,
  Truck,
  RotateCcw,
  Tag as TagIcon,
  ShoppingBag,
  ExternalLink,
  AlertTriangle,
  Plus,
  Layers,
  Archive,
  Scissors,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import {
  Page,
  Main,
  Card,
  Button,
  Badge,
  Modal,
  Field,
  inputClass,
  LoadingState,
  ErrorState,
  Thumb,
} from '@/components/ui';
import { StatusBadge } from './components/StatusBadge';
import {
  SendForServiceModal,
  MarkReturnedModal,
  MarkDeliveredModal,
  MarkPurchasedModal,
  RelocateModal,
  MarkSoldModal,
  LogWearModal,
} from './components/transitionModals';
import { AddTailorActionModal } from './components/AddTailorActionModal';
import api from '@/lib/api';
import { useApi, useMutation } from '@/hooks/useApi';
import { downscaleToBase64 } from '@/lib/image';
import {
  formatCurrency,
  formatDate,
  formatRelativeDate,
  formatWearCount,
  cn,
} from '@/lib/utils';
import type {
  CleaningLog,
  Closet,
  FitPiece,
  Item,
  ItemGalleryPhoto,
  SaleRecord,
  ServiceRecord,
  TailorAction,
  Tag,
} from '@/lib/types';

interface ItemDetail {
  item: Omit<Item, 'photos'> & { photos?: ItemGalleryPhoto[] };
  serviceHistory: ServiceRecord[];
  cleanings: CleaningLog[];
  sale: SaleRecord | null;
  statusHistory: unknown[];
  inFits: { id: string; name: string; pieces?: FitPiece[]; imageUrl?: string | null }[];
  costPerWear: number | null;
}

type ModalKind =
  | 'service'
  | 'returned'
  | 'delivered'
  | 'picked-up'
  | 'fitting-complete'
  | 'purchased'
  | 'relocate'
  | 'sold'
  | 'wear'
  | 'cutout'
  | 'tailor'
  | null;

export function ItemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mutate } = useMutation();
  const { data: closetsData } = useApi<{ closets: Closet[] }>('/closets');

  const [detail, setDetail] = useState<ItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modal, setModal] = useState<ModalKind>(null);
  const [busy, setBusy] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [affectedFits, setAffectedFits] = useState<{ id: string; name: string }[] | null>(null);
  const [tailorRefresh, setTailorRefresh] = useState(0);
  const [editTailor, setEditTailor] = useState<TailorAction | null>(null);

  const load = useMemo(
    () => async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api.get<ItemDetail>(`/items/${id}`);
        setDetail(res);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load item');
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Page>
        <PageHeader section="Item Details" backHref="/wardrobe/items" backLabel="Items" />
        <Main>
          <LoadingState />
        </Main>
      </Page>
    );
  }

  if (error || !detail) {
    return (
      <Page>
        <PageHeader section="Item Details" backHref="/wardrobe/items" backLabel="Items" />
        <Main>
          <ErrorState message={error ?? 'Item not found'} />
        </Main>
      </Page>
    );
  }

  const { item, serviceHistory, cleanings, sale, inFits } = detail;
  const isWishlist = item.status === 'wishlist';
  const closetCount = closetsData?.closets.length ?? 0;

  // Split the item's photos: garment shots feed the gallery + the "on me"
  // render; saved on-me renders (kind 'modeled') live in their own section.
  const photos = item.photos ?? [];
  const onMePhotos = photos.filter((p) => p.source === 'own' && p.kind === 'modeled');
  const hasGarment = photos.some((p) => p.source === 'own' && p.kind === 'item');

  const runTransition = async (action: string, payload: Record<string, unknown>) => {
    if (!id) return;
    setBusy(true);
    const res = await mutate('post', `/items/${id}/transition`, { action, ...payload });
    setBusy(false);
    if (res !== null) {
      setModal(null);
      load();
    }
  };

  const logWear = async (payload: Record<string, unknown>) => {
    if (!id) return;
    setBusy(true);
    const res = await mutate('post', `/items/${id}/wears`, payload);
    setBusy(false);
    if (res !== null) {
      setModal(null);
      load();
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setBusy(true);
    try {
      const res = await api.delete<{ ok: boolean; affectedFits?: { id: string; name: string }[] }>(
        `/items/${id}`,
      );
      if (res?.affectedFits?.length) {
        // surface but still navigate away
        setAffectedFits(res.affectedFits);
      }
      navigate('/wardrobe/items');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <PageHeader section="Item Details" backHref="/wardrobe/items" backLabel="Items" />
      <Main className="space-y-5">
        {/* Photo gallery */}
        <PhotoGallery itemId={item.id} photos={item.photos ?? []} onChange={load} />

        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-foreground leading-tight">{item.name}</h1>
            {item.brand && <p className="text-sm text-muted-foreground mt-0.5">{item.brand}</p>}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <StatusBadge status={item.status} />
              <Badge tone="neutral">{item.category}</Badge>
              {item.size && <Badge tone="neutral">{item.size}</Badge>}
            </div>
          </div>
          <Button
            variant="outline"
            className="px-3 py-2 flex-shrink-0"
            onClick={() => navigate(`/wardrobe/add?edit=${item.id}`)}
          >
            <Pencil className="w-4 h-4" />
            Edit
          </Button>
        </div>

        {/* Out-for-service warning */}
        {item.status === 'out-for-service' && (
          <Card className="p-4 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Item Out for Service</p>
                {serviceHistory.find((s) => s.status === 'in-progress') && (
                  <p className="text-xs text-amber-700 mt-1">
                    At {serviceHistory.find((s) => s.status === 'in-progress')?.providerName}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setModal('returned')}
                  className="text-xs font-semibold text-amber-800 underline mt-2"
                >
                  Mark as Returned
                </button>
              </div>
            </div>
          </Card>
        )}

        {/* AI actions: create a cutout, or render it on you */}
        <div className="space-y-3">
          <Button variant="outline" fullWidth onClick={() => setModal('cutout')}>
            <Scissors className="w-4 h-4" />
            Create cutout
          </Button>
          <OnMeSection itemId={item.id} saved={onMePhotos} canRender={hasGarment} onChange={load} />
        </div>

        {/* Wishlist actions */}
        {isWishlist && (
          <div className="space-y-3">
            <Button fullWidth onClick={() => setModal('purchased')}>
              <ShoppingBag className="w-4 h-4" />
              Mark Purchased
            </Button>
            {item.onlineStoreUrl && (
              <a
                href={item.onlineStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-3 bg-card border border-border rounded-xl text-sm font-medium text-foreground hover:border-primary/30 transition-all"
              >
                <ExternalLink className="w-4 h-4" />
                View in Store
              </a>
            )}
            <Button
              variant="outline"
              fullWidth
              onClick={() => runTransition('archive-wishlist', { reason: 'no-longer-wanted' })}
              disabled={busy}
            >
              <Archive className="w-4 h-4" />
              Archive from Wishlist
            </Button>
          </div>
        )}

        {/* In-closet actions */}
        {item.status === 'in-closet' && (
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => setModal('wear')}>
              <Star className="w-4 h-4" />
              Log Wear
            </Button>
            <Button variant="outline" onClick={() => setModal('service')}>
              <Truck className="w-4 h-4" />
              Send for Service
            </Button>
            <Button variant="outline" onClick={() => setModal('relocate')}>
              <RotateCcw className="w-4 h-4" />
              Relocate
            </Button>
            <Button variant="outline" onClick={() => setModal('tailor')}>
              <Scissors className="w-4 h-4" />
              Add Tailor Action
            </Button>
          </div>
        )}

        {/* Pending-delivery action */}
        {item.status === 'pending-delivery' && (
          <Button fullWidth onClick={() => setModal('delivered')}>
            <Truck className="w-4 h-4" />
            {item.pendingType === 'pickup'
              ? 'Mark Picked Up'
              : item.pendingType === 'fitting'
                ? 'Fitting Complete'
                : 'Mark Delivered'}
          </Button>
        )}

        {/* For-sale actions */}
        {item.status === 'for-sale' && (
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={() => setModal('sold')}>Mark Sold</Button>
            <Button
              variant="outline"
              onClick={() => runTransition('withdraw-listing', {})}
              disabled={busy}
            >
              Withdraw
            </Button>
          </div>
        )}

        {/* Details */}
        <Card className="p-4 space-y-2.5">
          <h3 className="text-sm font-semibold text-foreground mb-1">Details</h3>
          {!isWishlist && (
            <DetailRow
              label="Last Worn"
              value={item.lastWornAt ? formatRelativeDate(item.lastWornAt) : 'Never'}
              to={item.wearCount > 0 ? `/wardrobe/item/${item.id}/wears` : undefined}
            />
          )}
          {!isWishlist && <DetailRow label="Total Wears" value={formatWearCount(item.wearCount)} />}
          {item.isBespoke ? (
            <DetailRow
              label="Bespoke"
              value={item.bespokeDate ? `Made ${formatDate(item.bespokeDate)}` : 'Yes'}
            />
          ) : (
            item.size && <DetailRow label="Size" value={item.size} />
          )}
          <DetailRow
            label={item.colors && item.colors.length > 1 ? 'Colors' : 'Color'}
            value={
              item.colors && item.colors.length
                ? item.colors.map((c) => c.name).join(', ')
                : item.color
            }
            hex={item.colors && item.colors.length ? item.colors[0].hex : item.colorHex}
          />
          {item.mill && <DetailRow label="Mill" value={item.mill} />}
          <DetailRow label="Materials" value={item.materials.join(', ') || null} />
          <DetailRow
            label="Seasons"
            value={item.seasons.length ? item.seasons.join(', ') : null}
          />
          {item.subcategory && <DetailRow label="Type" value={item.subcategory} />}
          {(item.dimLength || item.dimWidth || item.dimHeight) && (
            <DetailRow
              label="Dimensions"
              value={`${item.dimLength ?? '?'} × ${item.dimWidth ?? '?'} × ${item.dimHeight ?? '?'} ${item.dimUnit ?? ''}`}
            />
          )}
          <DetailRow label="Cost" value={formatCurrency(item.cost, item.currency)} />
          <DetailRow label="Store" value={item.storeName} />
          {item.closetName && (
            <DetailRow
              label="Closet"
              value={item.closetName}
              onClick={closetCount > 1 ? () => setModal('relocate') : undefined}
            />
          )}
          {!isWishlist && <DetailRow label="Acquired" value={formatDate(item.acquisitionDate)} />}
          {item.deliveredDate && <DetailRow label="Delivered" value={formatDate(item.deliveredDate)} />}
          {item.status === 'pending-delivery' && item.expectedDeliveryDate && (
            <DetailRow label="Expected" value={formatDate(item.expectedDeliveryDate)} />
          )}
          {isWishlist && <DetailRow label="Added" value={formatDate(item.wishlistAddedDate)} />}
          {item.careInstructions.length > 0 && (
            <DetailRow label="Care" value={item.careInstructions.join(', ')} />
          )}
          {item.description && <DetailRow label="Description" value={item.description} />}
          {item.notes && <DetailRow label="Notes" value={item.notes} />}
        </Card>

        {/* Tags */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-foreground">Tags</h3>
            <button
              type="button"
              onClick={() => setShowTags(true)}
              className="text-xs font-medium text-primary inline-flex items-center gap-1"
            >
              <TagIcon className="w-3.5 h-3.5" />
              Edit
            </button>
          </div>
          {item.tags && item.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {item.tags.map((t) => (
                <Badge key={t.id} tone="accent">
                  {t.name}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No tags yet.</p>
          )}
        </Card>

        {/* In Fits */}
        {inFits.length > 0 && (
          <div>
            <h3 className="text-base font-semibold text-foreground uppercase tracking-wide mb-3 font-display">
              In Fits ({inFits.length})
            </h3>
            {item.status === 'archived' && (
              <Card className="p-3 mb-3 bg-amber-50 border-amber-200">
                <p className="text-xs text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  This item is archived. Open a fit below to replace this piece.
                </p>
              </Card>
            )}
            <div className="space-y-2">
              {inFits.map((fit) => (
                <Link key={fit.id} to={`/fits/${fit.id}`}>
                  <Card className="p-3 flex items-center gap-3">
                    <div className="p-2 bg-secondary rounded-lg">
                      <Layers className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-foreground flex-1">{fit.name}</span>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Tailor actions */}
        {id && (
          <TailorActionsSection
            itemId={id}
            refreshKey={tailorRefresh}
            onItemChanged={load}
            onEdit={setEditTailor}
          />
        )}

        {/* Service history */}
        {serviceHistory.length > 0 && (
          <div>
            <h3 className="text-base font-semibold text-foreground uppercase tracking-wide mb-3 font-display">
              Service History
            </h3>
            <Card className="overflow-hidden">
              {serviceHistory.map((s, i) => (
                <div
                  key={s.id}
                  className={cn('px-4 py-3', i < serviceHistory.length - 1 && 'border-b border-border')}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-foreground">{s.serviceType}</p>
                    {s.cost != null && (
                      <span className="text-sm font-medium text-foreground">
                        {formatCurrency(s.cost)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {s.providerName} · {formatDate(s.dropOffDate)}
                    {s.pickUpDate ? ` – ${formatDate(s.pickUpDate)}` : ' (in progress)'}
                  </p>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* Cleaning history */}
        {cleanings.length > 0 && (
          <div>
            <h3 className="text-base font-semibold text-foreground uppercase tracking-wide mb-3 font-display">
              Cleaning History
            </h3>
            <Card className="overflow-hidden">
              {cleanings.map((c, i) => (
                <div
                  key={c.id}
                  className={cn('px-4 py-3', i < cleanings.length - 1 && 'border-b border-border')}
                >
                  <p className="text-sm font-medium text-foreground">{c.method ?? 'Cleaned'}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{formatDate(c.cleanedDate)}</p>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* Sale record */}
        {sale && (
          <Card className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-2">Sale</h3>
            <DetailRow label="Status" value={sale.status} />
            <DetailRow label="List Price" value={formatCurrency(sale.listPrice)} />
            {sale.salePrice != null && (
              <DetailRow label="Sold For" value={formatCurrency(sale.salePrice)} />
            )}
            {sale.channel && <DetailRow label="Channel" value={sale.channel} />}
          </Card>
        )}

        {/* Delete */}
        <div className="space-y-3 pt-2">
          <Button variant="destructive" fullWidth onClick={() => setShowDelete(true)}>
            <Trash2 className="w-4 h-4" />
            Delete Item
          </Button>
        </div>
      </Main>

      {/* Transition modals */}
      <SendForServiceModal
        isOpen={modal === 'service'}
        onClose={() => setModal(null)}
        itemName={item.name}
        busy={busy}
        onConfirm={(p) => runTransition('send-for-service', p)}
      />
      <MarkReturnedModal
        isOpen={modal === 'returned'}
        onClose={() => setModal(null)}
        itemName={item.name}
        busy={busy}
        onConfirm={(p) => runTransition('mark-returned', p)}
      />
      <MarkDeliveredModal
        isOpen={modal === 'delivered'}
        onClose={() => setModal(null)}
        itemName={item.name}
        busy={busy}
        title={
          item.pendingType === 'pickup'
            ? 'Mark Picked Up'
            : item.pendingType === 'fitting'
              ? 'Fitting Complete'
              : 'Mark as Delivered'
        }
        onConfirm={(p) =>
          runTransition(
            item.pendingType === 'pickup'
              ? 'mark-picked-up'
              : item.pendingType === 'fitting'
                ? 'fitting-complete'
                : 'mark-delivered',
            p,
          )
        }
      />
      <MarkPurchasedModal
        isOpen={modal === 'purchased'}
        onClose={() => setModal(null)}
        itemName={item.name}
        busy={busy}
        onConfirm={(p) => runTransition('mark-purchased', p)}
      />
      <RelocateModal
        isOpen={modal === 'relocate'}
        onClose={() => setModal(null)}
        itemName={item.name}
        busy={busy}
        currentClosetId={item.closetId}
        onConfirm={(p) => runTransition('relocate', p)}
      />
      <MarkSoldModal
        isOpen={modal === 'sold'}
        onClose={() => setModal(null)}
        itemName={item.name}
        busy={busy}
        onConfirm={(p) => runTransition('mark-sold', p)}
      />
      <LogWearModal
        isOpen={modal === 'wear'}
        onClose={() => setModal(null)}
        itemName={item.name}
        busy={busy}
        onConfirm={logWear}
      />
      {id && (
        <CreateCutoutModal
          isOpen={modal === 'cutout'}
          onClose={() => setModal(null)}
          itemId={id}
          item={item}
          photos={photos.filter((p) => p.source === 'own' && p.kind !== 'modeled')}
          onDone={() => {
            setModal(null);
            load();
          }}
        />
      )}

      {id && (
        <AddTailorActionModal
          isOpen={modal === 'tailor' || !!editTailor}
          action={editTailor}
          onClose={() => {
            setModal(null);
            setEditTailor(null);
          }}
          itemId={id}
          onDone={() => {
            setModal(null);
            setEditTailor(null);
            setTailorRefresh((k) => k + 1);
          }}
        />
      )}

      {/* Delete confirm */}
      <Modal
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete Item?"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowDelete(false)}>
              Cancel
            </Button>
            <Button variant="destructive" fullWidth disabled={busy} onClick={handleDelete}>
              Delete
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          This permanently deletes “{item.name}”. This cannot be undone.
        </p>
        {inFits.length > 0 && (
          <p className="text-xs text-amber-700 mt-3">
            This item is used in {inFits.length} fit{inFits.length === 1 ? '' : 's'}. Those fits
            will need a replacement piece.
          </p>
        )}
        {affectedFits && affectedFits.length > 0 && (
          <p className="text-xs text-amber-700 mt-3">
            Affected fits: {affectedFits.map((f) => f.name).join(', ')}
          </p>
        )}
      </Modal>

      {/* Tags editor */}
      {showTags && id && (
        <TagsEditor
          itemId={id}
          current={item.tags ?? []}
          onClose={() => setShowTags(false)}
          onSaved={() => {
            setShowTags(false);
            load();
          }}
        />
      )}
    </Page>
  );
}

function DetailRow({
  label,
  value,
  hex,
  to,
  onClick,
}: {
  label: string;
  value: string | null | undefined;
  hex?: string | null;
  to?: string;
  onClick?: () => void;
}) {
  if (!value) return null;
  const content = (
    <span className="text-xs text-foreground font-medium text-right flex items-center gap-1.5">
      {hex && (
        <span
          className="w-3.5 h-3.5 rounded-full border border-border inline-block"
          style={{ backgroundColor: hex }}
        />
      )}
      {value}
      {(to || onClick) && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
    </span>
  );
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs text-muted-foreground flex-shrink-0">{label}</span>
      {to ? (
        <Link to={to}>{content}</Link>
      ) : onClick ? (
        <button type="button" onClick={onClick}>
          {content}
        </button>
      ) : (
        content
      )}
    </div>
  );
}

// Create a transparent-background cutout for this item from one of its photos
// (or a freshly uploaded one), then save it as the item's primary photo.
function CreateCutoutModal({
  isOpen,
  onClose,
  itemId,
  item,
  photos,
  onDone,
}: {
  isOpen: boolean;
  onClose: () => void;
  itemId: string;
  item: { name: string; category: string; colorHex: string | null };
  photos: ItemGalleryPhoto[];
  onDone: () => void;
}) {
  const [source, setSource] = useState<string | null>(null); // base64 data URL
  const [sourceLabel, setSourceLabel] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setSource(null);
    setSourceLabel('');
    setResult(null);
    setError(null);
  };

  const pickExisting = async (url: string) => {
    setError(null);
    try {
      const res = await fetch(url, { credentials: 'include' });
      const blob = await res.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result as string);
        fr.onerror = () => reject(new Error('read failed'));
        fr.readAsDataURL(blob);
      });
      setSource(dataUrl);
      setSourceLabel('Selected photo');
      setResult(null);
    } catch {
      setError('Could not load that photo.');
    }
  };

  const pickUpload = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const dataUrl = await downscaleToBase64(file);
      setSource(dataUrl);
      setSourceLabel(file.name);
      setResult(null);
    } catch {
      setError('Could not read that file.');
    }
  };

  const generate = async () => {
    if (!source) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await api.post<{ image: string }>('/ai/cutout', {
        image: source,
        metadata: { name: item.name, category: item.category, colorHex: item.colorHex },
      });
      setResult(res.image);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Cutout generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!result) return;
    setSaving(true);
    setError(null);
    try {
      await api.post('/photos', {
        ownerType: 'item',
        ownerId: itemId,
        image: result,
        kind: 'item',
        isPrimary: true,
      });
      reset();
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the cutout.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Create cutout"
    >
      <div className="space-y-3">
        {error && <p className="text-xs text-destructive">{error}</p>}

        {result ? (
          <>
            <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-foreground/5 flex items-center justify-center">
              <img src={result} alt="" className="max-w-full max-h-full object-contain" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" fullWidth onClick={() => setResult(null)} disabled={saving}>
                Back
              </Button>
              <Button fullWidth onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save as item photo'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              Pick a source photo, or upload one, then generate a clean transparent-background cutout.
            </p>
            {photos.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {photos.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickExisting(p.url)}
                    className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-border"
                  >
                    <img src={p.thumbnailUrl ?? p.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <label className="block">
              <div className="flex items-center justify-center gap-2 w-full py-2.5 bg-secondary rounded-xl text-sm font-medium text-foreground cursor-pointer">
                <Camera className="w-4 h-4" />
                Upload a photo
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => pickUpload(e.target.files?.[0])}
              />
            </label>
            {source && (
              <div className="flex items-center gap-3">
                <img src={source} alt="" className="w-16 h-16 rounded-lg object-cover" />
                <span className="text-xs text-muted-foreground flex-1 truncate">{sourceLabel}</span>
              </div>
            )}
            <Button fullWidth onClick={generate} disabled={!source || generating}>
              <Sparkles className="w-4 h-4" />
              {generating ? 'Generating…' : 'Generate cutout'}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Photo gallery with add / set-primary / delete
// ---------------------------------------------------------------------------

function PhotoGallery({
  itemId,
  photos,
  onChange,
}: {
  itemId: string;
  photos: ItemGalleryPhoto[];
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Own garment/wear photos first (primary leads), then read-only fit-derived
  // wear photos. Saved "on me" renders (kind 'modeled') are shown separately.
  const own = photos
    .filter((p) => p.source === 'own' && p.kind !== 'modeled')
    .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
  const fromFits = photos.filter((p) => p.source === 'fit');
  const sorted = [...own, ...fromFits];
  const primaryCount = own.filter((p) => p.isPrimary).length;

  const safeIndex = sorted.length ? Math.min(activeIndex, sorted.length - 1) : 0;
  const active = sorted[safeIndex];
  const cycle = (delta: number) =>
    setActiveIndex(() => {
      const n = sorted.length;
      if (n === 0) return 0;
      return (safeIndex + delta + n) % n;
    });

  const addPhotos = async (files: FileList | null) => {
    if (!files) return;
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        const image = await downscaleToBase64(file);
        let kind: 'item' | 'wear' = 'item';
        try {
          const d = await api.post<{ detectedType: string }>('/ai/detect', { image });
          kind = d.detectedType === 'fit' ? 'wear' : 'item';
        } catch {
          /* default item */
        }
        await api.post(`/items/${itemId}/photos`, { image, isPrimary: own.length === 0, kind });
      }
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const setPrimary = async (photoId: string) => {
    await api.patch(`/photos/${photoId}`, { isPrimary: true });
    onChange();
  };
  const setKind = async (photoId: string, kind: 'item' | 'wear') => {
    await api.patch(`/photos/${photoId}`, { kind });
    onChange();
  };
  const remove = async (photoId: string) => {
    await api.delete(`/photos/${photoId}`);
    onChange();
  };

  return (
    <div className="space-y-3">
      {active && (
        // object-contain shows the whole photo (no auto-crop), letterboxed.
        <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-foreground/5 flex items-center justify-center">
          <img src={active.url} alt="" className="max-w-full max-h-full object-contain" />
          <div className="absolute top-2 left-2 flex gap-1.5">
            {active.source === 'own' && active.isPrimary && (
              <span className="px-2 py-0.5 bg-primary text-primary-foreground text-[10px] font-medium rounded-full">
                Main
              </span>
            )}
            <span className="px-2 py-0.5 bg-foreground/60 text-white text-[10px] font-medium rounded-full">
              {active.kind === 'wear' ? 'Wear' : 'Item'} shot
            </span>
            {active.source === 'fit' && (
              <span className="px-2 py-0.5 bg-accent/80 text-accent-foreground text-[10px] font-medium rounded-full">
                {active.fitName ? `From ${active.fitName}` : 'From a fit'}
                {active.wornDate ? ` · ${formatDate(active.wornDate)}` : ''}
              </span>
            )}
          </div>
          {sorted.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => cycle(-1)}
                aria-label="Previous photo"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-card/85 border border-border flex items-center justify-center hover:bg-card transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-foreground" />
              </button>
              <button
                type="button"
                onClick={() => cycle(1)}
                aria-label="Next photo"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-card/85 border border-border flex items-center justify-center hover:bg-card transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-foreground" />
              </button>
              <span className="absolute top-2 right-2 px-2 py-0.5 bg-foreground/60 text-white text-[10px] font-medium rounded-full">
                {safeIndex + 1} / {sorted.length}
              </span>
            </>
          )}
          {/* Kind toggle / set-main for the active OWN photo */}
          {active.source === 'own' && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2">
              <div className="flex rounded-full overflow-hidden border border-white/40 text-[10px] font-medium">
                {(['item', 'wear'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setKind(active.id, k)}
                    className={cn(
                      'px-2.5 py-1',
                      active.kind === k ? 'bg-primary text-primary-foreground' : 'bg-card/85 text-foreground',
                    )}
                  >
                    {k === 'item' ? 'Item' : 'Wear'}
                  </button>
                ))}
              </div>
              {(!active.isPrimary || primaryCount > 1) && (
                <button
                  type="button"
                  onClick={() => setPrimary(active.id)}
                  className="px-2.5 py-1 rounded-full bg-card/85 border border-border text-[10px] font-medium text-foreground"
                >
                  Set Main
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 overflow-x-auto pb-2">
        {sorted.map((p, idx) => (
          <div key={p.id} className="relative flex-shrink-0 w-20 h-20">
            <button
              type="button"
              onClick={() => setActiveIndex(idx)}
              className={cn(
                'block w-20 h-20 rounded-xl overflow-hidden',
                idx === safeIndex && 'ring-2 ring-primary ring-offset-1',
              )}
            >
              <Thumb url={p.thumbnailUrl ?? p.url} alt="" className="w-20 h-20" />
            </button>
            {p.source === 'own' ? (
              <button
                type="button"
                onClick={() => remove(p.id)}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center hover:bg-destructive hover:text-white transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : (
              <span className="absolute bottom-1 left-1 right-1 px-1 py-0.5 bg-foreground/60 text-white text-[8px] font-medium rounded text-center truncate">
                {p.wornDate ? formatDate(p.wornDate) : 'Fit'}
              </span>
            )}
          </div>
        ))}
        <label
          className={cn(
            'flex-shrink-0 w-20 h-20 rounded-xl border-2 border-dashed border-border bg-secondary/50 flex flex-col items-center justify-center gap-1 hover:border-primary/30 cursor-pointer transition-all',
            busy && 'opacity-50 pointer-events-none',
          )}
        >
          {own.length === 0 ? (
            <Plus className="w-6 h-6 text-muted-foreground" />
          ) : (
            <Camera className="w-5 h-5 text-muted-foreground" />
          )}
          <span className="text-[9px] font-medium text-muted-foreground">{busy ? '…' : 'Add'}</span>
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => addPhotos(e.target.files)}
          />
        </label>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// "See it on you" — render the user's avatar wearing this one item
// ---------------------------------------------------------------------------

function OnMeSection({
  itemId,
  saved,
  canRender,
  onChange,
}: {
  itemId: string;
  saved: ItemGalleryPhoto[];
  canRender: boolean;
  onChange: () => void;
}) {
  const [rendering, setRendering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Nothing to show or do (no garment photo yet and nothing saved).
  if (!canRender && saved.length === 0) return null;

  const render = async () => {
    setRendering(true);
    setError(null);
    try {
      const res = await api.post<{ image: string }>('/ai/item-render', { itemId });
      setPreview(res.image);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not render this item.');
    } finally {
      setRendering(false);
    }
  };

  const save = async () => {
    if (!preview) return;
    setSaving(true);
    setError(null);
    try {
      await api.post('/photos', { ownerType: 'item', ownerId: itemId, image: preview, kind: 'modeled' });
      setPreview(null);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save this render.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (photoId: string) => {
    await api.delete(`/photos/${photoId}`);
    onChange();
  };

  const cta = rendering ? 'Rendering…' : saved.length || preview ? 'Render again' : 'See it on me';

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">See it on you</h3>
        {canRender && (
          <Button variant="outline" className="px-3 py-2" onClick={render} disabled={rendering || saving}>
            <Sparkles className="w-4 h-4" />
            {cta}
          </Button>
        )}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      {preview && (
        <div className="space-y-2">
          <div className="relative w-full aspect-[2/3] rounded-2xl overflow-hidden bg-foreground/5 flex items-center justify-center">
            <img src={preview} alt="" className="max-w-full max-h-full object-contain" />
            <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary/10 text-primary text-[10px] font-medium rounded-full">
              Preview
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setPreview(null)} disabled={saving}>
              Discard
            </Button>
            <Button fullWidth onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save to item'}
            </Button>
          </div>
        </div>
      )}

      {saved.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {saved.map((p) => (
            <div key={p.id} className="relative flex-shrink-0 w-24">
              <div className="w-24 aspect-[2/3] rounded-xl overflow-hidden bg-foreground/5">
                <img src={p.thumbnailUrl ?? p.url} alt="" className="w-full h-full object-cover" />
              </div>
              <button
                type="button"
                onClick={() => remove(p.id)}
                aria-label="Delete render"
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center hover:bg-destructive hover:text-white transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!canRender && saved.length > 0 && (
        <p className="text-xs text-muted-foreground">Add a garment photo to render a new look.</p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tailor actions — this item's alterations (history + pending/out workflow)
// ---------------------------------------------------------------------------

function TailorActionsSection({
  itemId,
  refreshKey,
  onItemChanged,
  onEdit,
}: {
  itemId: string;
  refreshKey: number;
  onItemChanged: () => void;
  onEdit: (action: TailorAction) => void;
}) {
  const [actions, setActions] = useState<TailorAction[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refetch = useMemo(
    () => async () => {
      try {
        const res = await api.get<{ tailorActions: TailorAction[] }>(`/items/${itemId}/tailor-actions`);
        setActions(res.tailorActions);
      } catch {
        setActions([]);
      }
    },
    [itemId],
  );

  useEffect(() => {
    refetch();
  }, [refetch, refreshKey]);

  const run = async (id: string, fn: () => Promise<unknown>, changesItem: boolean) => {
    setBusyId(id);
    try {
      await fn();
      await refetch();
      if (changesItem) onItemChanged();
    } finally {
      setBusyId(null);
    }
  };

  if (!actions || actions.length === 0) return null;

  return (
    <Card className="p-4 space-y-3">
      <h3 className="text-sm font-semibold text-foreground">Tailor Actions</h3>
      <div className="space-y-2">
        {actions.map((a) => (
          <div key={a.id} className="rounded-xl border border-border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-foreground">{a.actionType}</span>
              <Badge tone={a.status === 'out' ? 'accent' : 'neutral'}>
                {a.status === 'pending' ? 'Pending' : a.status === 'out' ? 'At tailor' : 'Completed'}
              </Badge>
            </div>
            {(a.completedDate || a.outDate || a.actionDate) && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {a.status === 'completed' && a.completedDate
                  ? `Done ${formatDate(a.completedDate)}`
                  : a.status === 'out' && a.outDate
                    ? `Sent ${formatDate(a.outDate)}`
                    : a.actionDate
                      ? `Target ${formatDate(a.actionDate)}`
                      : ''}
              </p>
            )}
            {a.notes && <p className="text-xs text-muted-foreground mt-0.5">{a.notes}</p>}
            <div className="flex items-center gap-3 mt-2">
              {a.status === 'pending' && (
                <Button
                  variant="outline"
                  className="px-3 py-1.5 text-xs"
                  disabled={busyId === a.id}
                  onClick={() => run(a.id, () => api.post('/tailor-actions/take-to-tailor', { ids: [a.id] }), true)}
                >
                  Take to tailor
                </Button>
              )}
              {a.status === 'out' && (
                <Button
                  variant="outline"
                  className="px-3 py-1.5 text-xs"
                  disabled={busyId === a.id}
                  onClick={() => run(a.id, () => api.post('/tailor-actions/complete', { ids: [a.id] }), true)}
                >
                  Mark returned
                </Button>
              )}
              <div className="ml-auto flex items-center gap-3">
                <button
                  type="button"
                  disabled={busyId === a.id}
                  onClick={() => onEdit(a)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Edit
                </button>
                {a.status !== 'out' && (
                  <button
                    type="button"
                    disabled={busyId === a.id}
                    onClick={() => run(a.id, () => api.delete(`/tailor-actions/${a.id}`), false)}
                    className="text-xs text-muted-foreground hover:text-destructive"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tags editor modal
// ---------------------------------------------------------------------------

function TagsEditor({
  itemId,
  current,
  onClose,
  onSaved,
}: {
  itemId: string;
  current: Tag[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set(current.map((t) => t.id)));
  const [newTag, setNewTag] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ tags: Tag[] }>('/tags')
      .then((res) => setAllTags(res.tags))
      .catch(() => setAllTags([]));
  }, []);

  const toggle = (tagId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  };

  const createTag = async () => {
    const name = newTag.trim();
    if (!name) return;
    const res = await api.post<{ id: string; name: string; color: string | null }>('/tags', { name });
    setAllTags((prev) => [...prev, { id: res.id, name: res.name, color: res.color }]);
    setSelected((prev) => new Set(prev).add(res.id));
    setNewTag('');
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/items/${itemId}/tags`, { tagIds: Array.from(selected) });
      onSaved();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Edit Tags"
      footer={
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button fullWidth disabled={busy} onClick={save}>
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {allTags.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => toggle(t.id)}
              className={cn(
                'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                selected.has(t.id)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-foreground',
              )}
            >
              {t.name}
            </button>
          ))}
          {allTags.length === 0 && <p className="text-sm text-muted-foreground">No tags yet.</p>}
        </div>
        <Field label="Create a tag">
          <div className="flex gap-2">
            <input
              className={inputClass}
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="New tag name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  createTag();
                }
              }}
            />
            <Button variant="outline" onClick={createTag}>
              Add
            </Button>
          </div>
        </Field>
      </div>
    </Modal>
  );
}
